import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch, useAuth } from '../App';
import { Input } from './ui/input';
import { Music, Play, Pause, Info, Pencil, Check, X, Lock, Unlock, Download, Trash2, Loader2, AlertTriangle, Star, Archive, Search } from 'lucide-react';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';

export default function SongsSidebar({ currentSongId = null, refreshTrigger = 0, maxHeight = null }) {
  const navigate = useNavigate();
  const { user, loading: authLoading, refreshUser } = useAuth();
  const [recentSongs, setRecentSongs] = useState([]);
  // v2.9.49 — user-toggleable sort for the right sidebar. Default "newest"
  // matches the previous behaviour (server returns created_at desc). Saved
  // to localStorage so the choice sticks across page loads.
  const [sortMode, setSortMode] = useState(() => {
    try {
      return localStorage.getItem('wim_sidebar_sort') || 'newest';
    } catch {
      return 'newest';
    }
  });
  // v2.9.50 — Song Organization mirrored in the sidebar so heavy users
  // can pin / archive / search without leaving the song detail page.
  // Shares the same localStorage key as the Dashboard so the pill choice
  // follows the user around.
  const [categoryFilter, setCategoryFilter] = useState(() => {
    try {
      return localStorage.getItem('wim_song_category') || 'all';
    } catch {
      return 'all';
    }
  });
  // v2.9.52 — Mirror Dashboard mood + genre dropdowns. Share localStorage
  // keys so the user's choice follows them across surfaces.
  const [moodFilter, setMoodFilter] = useState(() => {
    try { return localStorage.getItem('wim_song_mood') || ''; } catch { return ''; }
  });
  const [genreFilter, setGenreFilter] = useState(() => {
    try { return localStorage.getItem('wim_song_genre') || ''; } catch { return ''; }
  });
  const [moodOptions, setMoodOptions] = useState(['agressive', 'dark', 'energetic', 'hopeful', 'melancholic', 'nostalgic', 'peaceful', 'romantic', 'upbeat']);
  const [genreOptions, setGenreOptions] = useState(['acoustic', 'classical', 'country', 'country-rock', 'electronic', 'gospel', 'hip-hop', 'indie', 'jazz', 'metal', 'pop', 'r&b', 'rock', 'smooth jazz']);
  const [songSearch, setSongSearch] = useState('');
  const [togglingFavId, setTogglingFavId] = useState(null);
  const [editingSongId, setEditingSongId] = useState(null);
  const [editingSongTitle, setEditingSongTitle] = useState('');
  const [unlockingSongId, setUnlockingSongId] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, songId: null, title: '' });
  const [unlockCost, setUnlockCost] = useState(20);
  
  // Audio player state
  const audioRef = useRef(null);
  const [playingSongId, setPlayingSongId] = useState(null);

  const fetchRecentSongs = useCallback(async () => {
    try {
      const [songsRes, costsRes] = await Promise.all([
        apiFetch('/songs'),
        apiFetch('/credits/costs').catch(() => ({ data: {} }))
      ]);
      setRecentSongs(songsRes.data); // Show all user songs
      if (costsRes.data?.song_unlock !== undefined) {
        setUnlockCost(costsRes.data.song_unlock);
      }
    } catch (error) {
      // v2.9.45: Don't call refreshUser on 401 — that triggers checkAuth
      // which can clear the token after 2 consecutive 401s and bounce the
      // user back to "/" mid-render. Sidebar fetches are background work;
      // just leave `recentSongs` as-is and the next render will retry.
      if (error?.response?.status === 401) {
        return;
      }
      console.error('Error fetching songs:', error);
    }
  }, []);

  useEffect(() => {
    // Gate on auth ready, same as Dashboard.
    if (authLoading || !user) return;
    fetchRecentSongs();
  }, [fetchRecentSongs, refreshTrigger, authLoading, user]); // Re-fetch when refreshTrigger changes

  useEffect(() => {
    if (authLoading || !user) return;
    fetchRecentSongs();
  }, [fetchRecentSongs, authLoading, user]);

  const handleSaveSongTitle = async (songId) => {
    if (!editingSongTitle.trim()) {
      toast.error('Title cannot be empty');
      return;
    }
    try {
      await apiFetch(`/songs/${songId}/title`, {
        method: 'PUT',
        body: JSON.stringify({ title: editingSongTitle.trim() })
      });
      setRecentSongs(recentSongs.map(s => 
          s.song_id === songId ? { ...s, title: editingSongTitle.trim() } : s
        ));
        setEditingSongId(null);
        toast.success('Title updated!');
    } catch (error) {
      toast.error('Failed to update title');
    }
  };

  // Handle song unlock
  const handleUnlockSong = async (songId) => {
    setUnlockingSongId(songId);
    try {
      const { data } = await apiFetch(`/songs/${songId}/unlock`, { method: 'POST' });
      toast.success(data.message || 'Song unlocked!');
      refreshUser();
      setRecentSongs(recentSongs.map(s => 
        s.song_id === songId ? { ...s, locked: false } : s
      ));
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || 'Unlock failed');
    } finally {
      setUnlockingSongId(null);
    }
  };

  // Handle play/pause audio
  const handlePlayPause = (song) => {
    if (playingSongId === song.song_id) {
      // Pause current song
      audioRef.current?.pause();
      setPlayingSongId(null);
    } else {
      // Play new song
      if (audioRef.current) {
        audioRef.current.src = song.audio_url;
        audioRef.current.play();
        setPlayingSongId(song.song_id);
      }
    }
  };

  // Handle song delete
  const handleConfirmDelete = async () => {
    const { songId } = deleteDialog;
    setDeleteDialog({ open: false, songId: null, title: '' });
    
    try {
      console.log('Deleting song:', songId);
      const result = await apiFetch(`/songs/${songId}`, { method: 'DELETE' });
      console.log('Delete response:', result);
      setRecentSongs(recentSongs.filter(s => s.song_id !== songId));
      toast.success('Song deleted permanently');
    } catch (error) {
      console.error('Delete failed:', error);
      toast.error('Failed to delete song');
    }
  };

  // Apply user-selected sort. Server returns newest-first by default; we
  // re-sort client-side. Locale-aware comparison so "Ștefan" lands near
  // "Stefan" instead of after Z. Same `Intl.Collator` pattern used in the
  // Admin tables (v2.9.41 / 42).
  // v2.9.50 — Also apply category pill + free-text search before sorting.
  // v2.9.52 — Plus Mood + Genre dropdowns. All filters combine with AND.
  const displayedSongs = React.useMemo(() => {
    const q = songSearch.trim().toLowerCase();
    const mood = moodFilter.trim().toLowerCase();
    const genre = genreFilter.trim().toLowerCase();
    const catOf = (s) => (s.category || 'default');
    let list = recentSongs.filter(s => {
      if (categoryFilter === 'all') {
        if (catOf(s) === 'archive') return false; // hide archive from "All"
      } else if (categoryFilter === 'favorite') {
        if (catOf(s) !== 'favorite') return false;
      } else if (categoryFilter === 'archive') {
        if (catOf(s) !== 'archive') return false;
      }
      if (mood) {
        const songMood = (s.mood || '').toLowerCase();
        if (songMood !== mood) {
          const blob = [s.prompt, s.style].filter(Boolean).join(' ').toLowerCase();
          if (!blob.includes(mood)) return false;
        }
      }
      if (genre) {
        const songGenre = (s.genre || '').toLowerCase();
        if (songGenre !== genre) {
          const blob = [s.prompt, s.style].filter(Boolean).join(' ').toLowerCase();
          if (!blob.includes(genre)) return false;
        }
      }
      if (q) {
        const blob = [s.title, s.prompt, s.style, s.mood, s.genre]
          .filter(Boolean).join(' ').toLowerCase();
        if (!blob.includes(q)) return false;
      }
      return true;
    });
    if (sortMode === 'newest') return list; // server already sorted desc
    if (sortMode === 'oldest') {
      return list.sort((a, b) => (a.created_at || '').localeCompare(b.created_at || ''));
    }
    const collator = new Intl.Collator(undefined, { sensitivity: 'base', numeric: true });
    const titleKey = (s) => (s.title || '~~~').trim().toLowerCase();
    if (sortMode === 'az') {
      return list.sort((a, b) => collator.compare(titleKey(a), titleKey(b)));
    }
    if (sortMode === 'za') {
      return list.sort((a, b) => collator.compare(titleKey(b), titleKey(a)));
    }
    return list;
  }, [recentSongs, sortMode, categoryFilter, moodFilter, genreFilter, songSearch]);

  // Counts for filter pills (live).
  const sidebarCounts = React.useMemo(() => {
    const counts = { all: 0, favorite: 0, archive: 0 };
    for (const s of recentSongs) {
      const c = s.category || 'default';
      if (c === 'archive') counts.archive += 1;
      else { counts.all += 1; if (c === 'favorite') counts.favorite += 1; }
    }
    return counts;
  }, [recentSongs]);

  const handleSortChange = (mode) => {
    setSortMode(mode);
    try { localStorage.setItem('wim_sidebar_sort', mode); } catch {}
  };

  const handleCategoryChange = (cat) => {
    setCategoryFilter(cat);
    try { localStorage.setItem('wim_song_category', cat); } catch {}
  };

  const handleMoodChange = (v) => {
    setMoodFilter(v);
    try { localStorage.setItem('wim_song_mood', v); } catch {}
  };

  const handleGenreChange = (v) => {
    setGenreFilter(v);
    try { localStorage.setItem('wim_song_genre', v); } catch {}
  };

  // v2.9.52 — Fetch mood/genre options on mount, fall back to defaults.
  useEffect(() => {
    if (authLoading || !user) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await apiFetch('/song-options');
        if (cancelled) return;
        if (data?.moods?.length) setMoodOptions([...data.moods].sort());
        if (data?.genres?.length) setGenreOptions([...data.genres].sort());
      } catch (e) {
        // Non-critical
      }
    })();
    return () => { cancelled = true; };
  }, [authLoading, user]);

  // ★ favorite toggle (sidebar version — same backend endpoint).
  const handleToggleFavorite = async (song, e) => {
    if (e) e.stopPropagation();
    const next = (song.category === 'favorite') ? 'default' : 'favorite';
    setTogglingFavId(song.song_id);
    setRecentSongs(prev => prev.map(s => s.song_id === song.song_id ? { ...s, category: next } : s));
    try {
      await apiFetch(`/songs/${song.song_id}/category`, {
        method: 'PATCH',
        body: JSON.stringify({ category: next }),
      });
      toast.success(next === 'favorite' ? 'Added to favorites' : 'Removed from favorites');
    } catch (err) {
      setRecentSongs(prev => prev.map(s => s.song_id === song.song_id ? { ...s, category: song.category } : s));
      toast.error('Failed to update favorite');
    } finally {
      setTogglingFavId(null);
    }
  };

  return (
    <div className="hidden lg:block w-80 flex-shrink-0">
      {/* Hidden Audio Element */}
      <audio 
        ref={audioRef} 
        onEnded={() => setPlayingSongId(null)}
        onError={() => setPlayingSongId(null)}
      />
      
      <div className="glass-card p-6 overflow-hidden flex flex-col" style={{ maxHeight: maxHeight || undefined }}>
        <div className="flex items-center gap-3 mb-6 flex-shrink-0">
          <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center">
            <Music className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-white">🎧 Your Songs</h3>
            <p className="text-sm text-zinc-400">Ready to play</p>
          </div>
        </div>

        {/* Sort toggle (v2.9.49) — choice persisted in localStorage */}
        {recentSongs.length > 1 && (
          <div className="flex items-center gap-1 mb-3 flex-shrink-0 text-[11px]" data-testid="sidebar-sort-toggle">
            <span className="text-zinc-500 mr-1">Sort:</span>
            {[
              { id: 'newest', label: 'Newest' },
              { id: 'oldest', label: 'Oldest' },
              { id: 'az', label: 'A→Z' },
              { id: 'za', label: 'Z→A' },
            ].map((opt) => (
              <button
                key={opt.id}
                type="button"
                data-testid={`sidebar-sort-${opt.id}`}
                onClick={() => handleSortChange(opt.id)}
                className={`px-2 py-1 rounded transition-colors ${
                  sortMode === opt.id
                    ? 'bg-primary/20 text-primary'
                    : 'text-zinc-400 hover:text-white hover:bg-white/5'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        )}

        {/* v2.9.50 — Category pills (mirrors Dashboard). Shown only when the
            user has enough songs to make organization meaningful. */}
        {recentSongs.length > 2 && (
          <div className="flex items-center gap-1 mb-2 flex-shrink-0 text-[11px]" data-testid="sidebar-filter-pills">
            {[
              { id: 'all', label: 'All', icon: null, count: sidebarCounts.all },
              { id: 'favorite', label: 'Favs', icon: Star, count: sidebarCounts.favorite },
              { id: 'archive', label: 'Arch.', icon: Archive, count: sidebarCounts.archive },
            ].map((opt) => {
              const Icon = opt.icon;
              const active = categoryFilter === opt.id;
              return (
                <button
                  key={opt.id}
                  type="button"
                  data-testid={`sidebar-filter-${opt.id}`}
                  onClick={() => handleCategoryChange(opt.id)}
                  className={`px-2 py-1 rounded flex items-center gap-1 transition-colors ${
                    active
                      ? 'bg-primary/20 text-primary'
                      : 'text-zinc-400 hover:text-white hover:bg-white/5'
                  }`}
                >
                  {Icon && <Icon className="h-3 w-3" fill={opt.id === 'favorite' && active ? 'currentColor' : 'none'} />}
                  {opt.label}
                  <span className={`text-[9px] ${active ? 'text-primary/70' : 'text-zinc-500'}`}>{opt.count}</span>
                </button>
              );
            })}
          </div>
        )}

        {/* v2.9.52 — Compact Mood + Genre dropdowns mirroring Dashboard.
            Shared localStorage so picks follow the user across surfaces. */}
        {recentSongs.length > 2 && (
          <div className="grid grid-cols-2 gap-1.5 mb-2 flex-shrink-0" data-testid="sidebar-filter-dropdowns">
            <select
              value={moodFilter}
              onChange={(e) => handleMoodChange(e.target.value)}
              data-testid="sidebar-mood-filter"
              className="bg-white/5 border border-white/10 rounded px-2 py-1 text-[11px] text-white focus:outline-none focus:border-primary/50 capitalize"
            >
              <option value="" className="bg-zinc-900 text-white">Any mood</option>
              {moodOptions.map((m) => (
                <option key={m} value={m} className="bg-zinc-900 text-white capitalize">{m}</option>
              ))}
            </select>
            <select
              value={genreFilter}
              onChange={(e) => handleGenreChange(e.target.value)}
              data-testid="sidebar-genre-filter"
              className="bg-white/5 border border-white/10 rounded px-2 py-1 text-[11px] text-white focus:outline-none focus:border-primary/50 capitalize"
            >
              <option value="" className="bg-zinc-900 text-white">Any genre</option>
              {genreOptions.map((g) => (
                <option key={g} value={g} className="bg-zinc-900 text-white capitalize">{g}</option>
              ))}
            </select>
          </div>
        )}

        {/* v2.9.50 — Search input. Always visible whenever the user has any
            songs — even just one — so muscle memory works. */}
        {recentSongs.length > 0 && (
          <div className="relative mb-3 flex-shrink-0" data-testid="sidebar-search-wrapper">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-zinc-500 pointer-events-none" />
            <input
              type="text"
              value={songSearch}
              onChange={(e) => setSongSearch(e.target.value)}
              placeholder="Search…"
              data-testid="sidebar-search-input"
              className="w-full bg-white/5 border border-white/10 rounded-md pl-8 pr-7 py-1.5 text-xs text-white placeholder:text-zinc-500 focus:outline-none focus:border-primary/50"
            />
            {songSearch && (
              <button
                type="button"
                onClick={() => setSongSearch('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white"
                data-testid="sidebar-search-clear"
              >
                <X className="h-3 w-3" />
              </button>
            )}
          </div>
        )}

        {recentSongs.length === 0 ? (
          <div className="glass-card p-4 text-center">
            <Music className="h-8 w-8 text-zinc-600 mx-auto mb-2" />
            <p className="text-sm text-zinc-400">No songs yet</p>
          </div>
        ) : displayedSongs.length === 0 ? (
          <div className="glass-card p-4 text-center" data-testid="sidebar-empty-filtered">
            <Music className="h-8 w-8 text-zinc-600 mx-auto mb-2" />
            <p className="text-sm text-zinc-400">
              {categoryFilter === 'favorite' && !moodFilter && !genreFilter && !songSearch && 'No favorites yet'}
              {categoryFilter === 'archive' && !moodFilter && !genreFilter && !songSearch && 'Nothing archived'}
              {(moodFilter || genreFilter || (categoryFilter !== 'favorite' && categoryFilter !== 'archive' && songSearch)) && 'No match'}
            </p>
            <button
              type="button"
              onClick={() => { handleCategoryChange('all'); handleMoodChange(''); handleGenreChange(''); setSongSearch(''); }}
              className="mt-2 text-xs text-primary hover:underline"
            >
              Clear filters
            </button>
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto space-y-3 pr-2 pb-5 min-h-0 scrollbar-thin scrollbar-thumb-white/20 scrollbar-track-transparent">
            {displayedSongs.map((song) => (
              <div 
                key={song.song_id}
                className={`glass-card p-3 hover:bg-white/10 transition-colors cursor-pointer ${
                  currentSongId === song.song_id ? 'ring-2 ring-primary bg-primary/10' : ''
                }`}
                onClick={() => navigate(`/song/${song.song_id}`)}
                data-testid={`sidebar-song-${song.song_id}`}
              >
                <div className="flex gap-3">
                  {/* Thumbnail with lock badge */}
                  <div className="w-16 h-16 rounded-lg bg-gradient-to-br from-primary/20 to-secondary/20 flex-shrink-0 overflow-hidden relative group/thumb">
                    {song.image_url ? (
                      <img src={song.image_url} alt={song.title} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Music className="h-6 w-6 text-white/30" />
                      </div>
                    )}
                    {/* Locked badge */}
                    {song.locked && (
                      <div className="absolute top-0.5 left-0.5 px-1 py-0.5 bg-yellow-500/90 text-black text-[8px] font-bold rounded flex items-center gap-0.5">
                        <Lock className="h-2 w-2" />
                      </div>
                    )}
                    {/* Action buttons on hover - play/unlock/download */}
                    <div className="absolute inset-0 bg-black/60 opacity-0 group-hover/thumb:opacity-100 transition-opacity flex items-center justify-center gap-1">
                      {song.status === 'completed' && song.audio_url && (
                        <>
                          {/* Play/Pause button */}
                          <button
                            onClick={(e) => { e.stopPropagation(); handlePlayPause(song); }}
                            className="p-1.5 bg-white/20 hover:bg-white/30 text-white rounded transition-colors"
                            title={playingSongId === song.song_id ? "Pause" : "Play"}
                          >
                            {playingSongId === song.song_id ? (
                              <Pause className="h-3 w-3" />
                            ) : (
                              <Play className="h-3 w-3" />
                            )}
                          </button>
                          
                          {song.locked ? (
                            <button
                              onClick={(e) => { e.stopPropagation(); handleUnlockSong(song.song_id); }}
                              disabled={unlockingSongId === song.song_id}
                              className="p-1.5 bg-yellow-500 hover:bg-yellow-400 text-black rounded transition-colors"
                              title={`Unlock (${unlockCost} credits)`}
                            >
                              {unlockingSongId === song.song_id ? (
                                <Loader2 className="h-3 w-3 animate-spin" />
                              ) : (
                                <Unlock className="h-3 w-3" />
                              )}
                            </button>
                          ) : (
                            <a
                              href={`${process.env.REACT_APP_BACKEND_URL}/api/songs/${song.song_id}/download-with-cover`}
                              download={`${song.title || 'song'}.mp3`}
                              onClick={(e) => e.stopPropagation()}
                              className="p-1.5 bg-green-500 hover:bg-green-400 text-black rounded transition-colors"
                              title="Download"
                            >
                              <Download className="h-3 w-3" />
                            </a>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    {editingSongId === song.song_id ? (
                      <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                        <Input
                          value={editingSongTitle}
                          onChange={(e) => setEditingSongTitle(e.target.value)}
                          className="input-dark text-sm h-7 py-0"
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleSaveSongTitle(song.song_id);
                            if (e.key === 'Escape') setEditingSongId(null);
                          }}
                        />
                        <button
                          onClick={() => handleSaveSongTitle(song.song_id)}
                          className="p-1 text-green-400 hover:text-green-300"
                        >
                          <Check className="h-3 w-3" />
                        </button>
                        <button
                          onClick={() => setEditingSongId(null)}
                          className="p-1 text-red-400 hover:text-red-300"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1 group/title">
                        <h4 className="text-sm font-medium text-white truncate flex-1">{song.title || 'Untitled'}</h4>
                        {/* v2.9.50 — quick favorite toggle in the sidebar */}
                        <button
                          onClick={(e) => handleToggleFavorite(song, e)}
                          disabled={togglingFavId === song.song_id}
                          className={`p-1 transition-colors ${
                            song.category === 'favorite'
                              ? 'text-yellow-400 hover:text-yellow-300'
                              : 'text-zinc-500 hover:text-yellow-400 opacity-0 group-hover/title:opacity-100'
                          }`}
                          title={song.category === 'favorite' ? 'Unfavorite' : 'Favorite'}
                          data-testid={`sidebar-fav-${song.song_id}`}
                        >
                          <Star className="h-3 w-3" fill={song.category === 'favorite' ? 'currentColor' : 'none'} />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingSongId(song.song_id);
                            setEditingSongTitle(song.title || 'Untitled');
                          }}
                          className="p-1 opacity-0 group-hover/title:opacity-100 text-zinc-400 hover:text-white transition-opacity"
                          title="Edit title"
                        >
                          <Pencil className="h-3 w-3" />
                        </button>
                      </div>
                    )}
                    <p className="text-xs text-zinc-400 mt-0.5">
                      {playingSongId === song.song_id ? 'Now playing...' : (song.status === 'completed' ? 'Ready to play' : song.status)}
                    </p>
                    {/* Action Buttons */}
                    <div className="flex gap-2 mt-2 items-center">
                      {song.status === 'completed' && song.audio_url && (
                        <button 
                          onClick={(e) => { e.stopPropagation(); handlePlayPause(song); }}
                          className="flex items-center gap-1 text-xs text-primary hover:text-primary/80"
                        >
                          {playingSongId === song.song_id ? (
                            <><Pause className="h-3 w-3" /> Pause</>
                          ) : (
                            <><Play className="h-3 w-3" /> Play</>
                          )}
                        </button>
                      )}
                      <button 
                        onClick={(e) => { e.stopPropagation(); navigate(`/song/${song.song_id}`); }}
                        className="flex items-center gap-1 text-xs text-zinc-400 hover:text-white"
                      >
                        <Info className="h-3 w-3" /> Details
                      </button>
                      <button 
                        onClick={(e) => { e.stopPropagation(); setDeleteDialog({ open: true, songId: song.song_id, title: song.title }); }}
                        className="flex items-center gap-1 text-xs text-zinc-400 hover:text-red-400 ml-auto"
                        title="Delete song"
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => !open && setDeleteDialog({ open: false, songId: null, title: '' })}>
        <AlertDialogContent className="bg-zinc-900 border-white/10">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-500" />
              Delete Song?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              <span className="block mb-2">
                Are you sure you want to delete "<span className="text-white font-medium">{deleteDialog.title}</span>"?
              </span>
              <span className="block text-red-400 font-medium">
                This action is permanent and cannot be undone.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-white/5 border-white/10 text-white hover:bg-white/10">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleConfirmDelete}
              className="bg-red-600 text-white hover:bg-red-700"
            >
              Delete Permanently
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
